import { cn } from "@/lib/utils";
import { PopoverContentProps, PopoverProps } from "@radix-ui/react-popover";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown } from "lucide-react";
import { ReactElement, useCallback, useMemo, useState } from "react";
import { Button, ButtonProps } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { useTranslation } from "react-i18next";

type KeyOfAString<T> = {
  [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

type ItemValue<T = unknown> = KeyOfAString<T> | ((i: T) => string);

type ItemContent<T = unknown> =
  | KeyOfAString<T>
  | ((i: T, currentValue?: string) => ReactElement);

export type SelectFetcherProps<FetchedType> = {
  popover?: PopoverProps;
  popoverTrigger?: ButtonProps;
  popovertContent?: PopoverContentProps;

  /** probably the id of the entity we're selecting on */
  getValue: ItemValue<FetchedType>;

  /** The label shown in the trigger. It's what we filter on (if not, we fallback to the value)*/
  getLabel?: ItemValue<FetchedType>;

  /** The content of each CommandItem*/
  getContent?: ItemContent<FetchedType>;

  triggerPlaceholder?: string;
  inputPlaceholder?: string;

  //** Load when opening the popover */
  lazy?: boolean;

  /** Allow clearing the selection by showing a "None" option */
  allowClear?: boolean;

  value?: string | null;
  onValueChange?: (value: string | null) => void;

  fetcher: () => Promise<FetchedType[]>;
  filter?: (query: string, search: string, keywords?: string[]) => number;
  queryKey?: string[];
};

export const SelectFetcher = <FetchedType,>({
  popover: { onOpenChange: onPopoverOpenChange, ...popoverProps } = {},
  popoverTrigger: { className: triggerClassName, ...popoverTrigger } = {},
  popovertContent: { className: popoverClassName, ...popoverContentProps } = {},
  getValue,
  getContent,
  getLabel,
  fetcher,
  queryKey,
  filter,
  lazy = false,
  triggerPlaceholder,
  value: inValue,
  onValueChange,
  allowClear,
}: SelectFetcherProps<FetchedType>) => {
  const value = inValue && inValue.length > 0 ? inValue : undefined;

  const {
    data: items = [],
    refetch,
    isFetched,
  } = useQuery({
    queryKey: [queryKey],
    queryFn: fetcher,
    enabled: !lazy,
  });

  const [open, setOpen] = useState(false);

  function onOpenChange(open: boolean) {
    if (open && !isFetched) {
      refetch();
    }
  }

  const extractItemLabel = useCallback(
    (item: FetchedType) => {
      if (!getLabel) return String(item);
      if (typeof getLabel === "function") return getLabel(item);
      const val = item[getLabel] as string;
      return val.length > 0 ? val : "?";
    },
    [getLabel],
  );

  const extractItemValue = useCallback(
    (item: FetchedType) => {
      if (!getValue) return String(item);
      if (typeof getValue === "function") return getValue(item);
      const val = item[getValue] as string;
      return val.length > 0 ? val : "?";
    },
    [getValue],
  );

  const extractItemContent = useCallback(
    (item: FetchedType) => {
      if (!getContent) return String(item);
      if (typeof getContent === "function") return getContent(item, value);
      return item[getContent] as string;
    },
    [getContent, value],
  );

  const selectedItem = useMemo(
    () =>
      value ? items.find((i) => extractItemValue(i) === value) : undefined,
    [value, items, extractItemValue],
  );

  const buttonContent = useMemo(() => {
    if (!selectedItem) return triggerPlaceholder;
    return getLabel ? extractItemLabel(selectedItem) : value;
  }, [extractItemLabel, getLabel, selectedItem, value, triggerPlaceholder]);

  const { t } = useTranslation();

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        setOpen(o);
        onPopoverOpenChange?.(o);
      }}
      {...popoverProps}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("min-w-[300px] w-fit justify-between", triggerClassName)}
          {...popoverTrigger}
        >
          {buttonContent}
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[300px] p-0", popoverClassName)}
        {...popoverContentProps}
      >
        <Command filter={filter}>
          <CommandInput className="h-9" />
          <CommandList>
            <CommandEmpty>{t("noItems")}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="None"
                  onSelect={() => {
                    onValueChange?.(null);
                    setOpen(false);
                  }}
                >
                  {"-"}
                </CommandItem>
              )}
              {items.map((i) => (
                <CommandItem
                  key={extractItemValue(i)}
                  value={getLabel ? extractItemLabel(i) : extractItemValue(i)}
                  className={
                    extractItemValue(i) === value ? "text-white bg-primary" : ""
                  }
                  onSelect={() => {
                    const itemValue = extractItemValue(i);

                    onValueChange?.(itemValue === value ? null : itemValue);

                    setOpen(false);
                  }}
                >
                  {extractItemContent(i)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
